import {SignOut, Star, UserCircle} from '@phosphor-icons/react';
import {Box, ButtonBase, Divider, Typography} from '@mui/material';
import {
  Station,
  UserInfo,
  getFavoriteStationList,
  selectUserInfo,
} from '@smartmixin/core';
import {messages} from '@smartmixin/i18n';
import React from 'react';
import isEqual from 'react-fast-compare';
import {useIntl} from 'react-intl';
import {useSelector} from 'react-redux';
import {useLocation} from 'react-router';
import Logo from '../../components/Logo';
import {IconButton} from '../../components/ui';
import RouterLink from '../../lib/RouterLink';
import {NavGroup, NavLink} from './NavLink';
import {ThemeToggleButton} from './ThemeToggleButton';
import {useLogout} from './useLogout';
import {NavItem, useNavItems} from './useNavItems';

/** Favourites shown before "See all": the sidebar is navigation, not a list. */
export const SIDEBAR_FAVORITES_MAX = 5;

const groupLabelSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  px: 1.25,
  minHeight: 34,
  color: 'text.secondary',
  fontSize: '0.875rem',
  fontWeight: 500,
  '& svg': {fontSize: 18},
} as const;

/**
 * The favourite stations, in the user's order: the first five, then a link
 * to the Stations page that lists them all. Nothing when there are none.
 */
const useFavoriteItems = () => {
  const {formatMessage} = useIntl();
  const favorites = useSelector(getFavoriteStationList);
  return React.useMemo(() => {
    const items: NavItem[] = favorites
      .slice(0, SIDEBAR_FAVORITES_MAX)
      .map((station) => {
        const to = `/app/stations/${station.id}`;
        return {
          key: `favorite-${station.id}`,
          label: Station.getStationName(station),
          icon: null,
          to,
          isActive: (p: string) => p === to || p.startsWith(`${to}/`),
        };
      });
    if (favorites.length > SIDEBAR_FAVORITES_MAX) {
      items.push({
        key: 'allFavorites',
        label: formatMessage(messages.navigation.allFavorites),
        icon: null,
        to: '/app/stations/',
        isActive: () => false,
      });
    }
    return items;
  }, [favorites, formatMessage]);
};

/**
 * The full navigation (232px): brand, destinations, help, admin, then add
 * station / settings / contact, and the account row with the theme toggle
 * and logout. Shared by the sidebar (≥1200) and the rail's
 * expanded panel (900–1199).
 */
export const SidebarContent = ({
  onNavigate,
  headerAction,
}: {
  onNavigate?: () => void;
  /** Beside the brand: the expanded rail's collapse control. */
  headerAction?: React.ReactNode;
}) => {
  const {formatMessage} = useIntl();
  const items = useNavItems();
  const {email, firstName, lastName} = useSelector<unknown, UserInfo>(
    selectUserInfo,
    isEqual,
  );
  const name = `${firstName ?? ''} ${lastName ?? ''}`.trim();

  const logout = useLogout();
  const {pathname} = useLocation();
  const favorites = useFavoriteItems();
  const favoritesLabelId = React.useId();
  // One current page: on a favourite's page the favourite is it, not
  // Stations (the phone bar, which has no favourites, keeps Stations).
  const onFavorite = favorites.some((item) => item.isActive(pathname));
  const primary = onFavorite
    ? items.primary.map((item) =>
        item.key === 'stations' ? {...item, isActive: () => false} : item,
      )
    : items.primary;

  return (
    <Box
      sx={{display: 'flex', flexDirection: 'column', height: '100%', p: 1.5}}>
      <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
        <Box
          component={RouterLink}
          to="/"
          onClick={onNavigate}
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1,
            py: 1,
            color: 'text.primary',
            textDecoration: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            borderRadius: 1,
          }}>
          <Logo size={24} alt="" />
          SmartMixin
        </Box>
        {headerAction}
      </Box>
      <Box
        component="nav"
        aria-label={formatMessage(messages.navigation.main)}
        sx={{display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 1}}>
        <NavGroup>
          {primary.map((item) => (
            <NavLink key={item.key} item={item} onNavigate={onNavigate} />
          ))}
          <NavLink item={items.help} onNavigate={onNavigate} />
          {items.admin.map((item) => (
            <NavLink key={item.key} item={item} onNavigate={onNavigate} />
          ))}
          {favorites.length > 0 && (
            <Box
              component="li"
              data-testid="shell-favorites"
              sx={{listStyle: 'none', mt: 1}}>
              <Typography
                component="span"
                id={favoritesLabelId}
                sx={groupLabelSx}>
                <Star />
                {formatMessage(messages.stations.favoriteStations)}
              </Typography>
              <NavGroup aria-labelledby={favoritesLabelId}>
                {favorites.map((item) => (
                  <NavLink
                    key={item.key}
                    item={item}
                    nested
                    onNavigate={onNavigate}
                  />
                ))}
              </NavGroup>
            </Box>
          )}
        </NavGroup>
        <Box sx={{flexGrow: 1}} />
        <NavGroup>
          {items.footer.map((item) => (
            <NavLink key={item.key} item={item} onNavigate={onNavigate} />
          ))}
        </NavGroup>
      </Box>
      <Divider sx={{my: 1}} />
      <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
        <ButtonBase
          component={RouterLink}
          to="/app/settings/account"
          onClick={onNavigate}
          disableRipple
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 0.75,
            borderRadius: 1,
            justifyContent: 'flex-start',
            textAlign: 'left',
            '&:hover': {backgroundColor: 'action.hover'},
          }}>
          <Box
            component="span"
            sx={{
              display: 'flex',
              color: 'primary.main',
              '& svg': {fontSize: 28},
            }}>
            <UserCircle weight="fill" />
          </Box>
          <Box component="span" sx={{minWidth: 0}}>
            <Typography
              component="span"
              noWrap
              sx={{display: 'block', fontSize: '0.8125rem', fontWeight: 600}}>
              {name || email}
            </Typography>
            {name && (
              <Typography
                component="span"
                noWrap
                sx={{
                  display: 'block',
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                }}>
                {email}
              </Typography>
            )}
          </Box>
        </ButtonBase>
        <ThemeToggleButton />
        <IconButton
          aria-label={formatMessage(messages.auth.logout)}
          onClick={logout}>
          <SignOut />
        </IconButton>
      </Box>
    </Box>
  );
};
